// ANALYTICS-001 client (Phase 7): report topics, exports, the Today widget layout, the consent
// mode and the leaderboard band. Shapes mirror packages/domain/analytics/src/{reports,exports,
// layouts,leaderboard,api}.ts.
import { useQuery } from "@tanstack/react-query";
import { request } from "../api/client";
import type { Page } from "../api/types";

export const TOPICS = [
  "sales",
  "products",
  "customers",
  "payments",
  "returns",
  "delivery",
  "traffic",
  "profit",
  "inventory",
  "discounts",
] as const;
export type Topic = (typeof TOPICS)[number];

export type Dim =
  | "total"
  | "channel"
  | "method"
  | "product"
  | "category"
  | "staff"
  | "location"
  | "district"
  | "courier"
  | "coupon"
  | "source"
  | "page"
  | "device";

export type Figures = Record<string, number>;
export type SeriesPoint = { date: string } & Record<string, number | string>;
export type BreakdownRow = { key: string; label: string | null } & Record<
  string,
  number | string | null
>;

export interface Report {
  topic: Topic;
  from: string;
  to: string;
  dim: Dim;
  tier: "basic" | "advanced";
  limits: {
    max_range_days: number;
    lookback_days: number | null;
    dims: Dim[];
    advanced_dims: Dim[];
  };
  updated_at: string | null;
  totals: Figures;
  series: SeriesPoint[];
  rows: BreakdownRow[];
  previous: Figures | null;
  availability: "available" | "activation_required";
  dependency?: string;
  partial?: boolean;
}

export interface ReportExport {
  id: string;
  topic: Topic;
  from: string;
  to: string;
  dim: Dim;
  state: "queued" | "running" | "done" | "failed";
  rows: number;
  failure_reason: string | null;
  created_at: string;
  finished_at: string | null;
  expires_at: string | null;
  download?: { url: string; expires_at: string } | null;
}

export const WIDGET_IDS = [
  "kpis",
  "sales_trend",
  "collected_outstanding",
  "top_products",
  "payment_methods",
  "courier_performance",
  "returns",
  "traffic_funnel",
  "leaderboard",
] as const;
export type WidgetId = (typeof WIDGET_IDS)[number];

export interface Layout {
  widgets: WidgetId[];
  catalogue: WidgetId[];
  is_default: boolean;
  version: number;
  updated_at: string | null;
}

export type Band = "top_10" | "top_25" | "top_50" | "bottom_50";
export interface Leaderboard {
  opted_in: boolean;
  opted_in_at: string | null;
  min_cohort: number;
  band:
    | {
        state: "ready";
        period: { from: string; to: string };
        cohort_size: number;
        orders: Band;
        gmv: Band;
      }
    | { state: "not_enough_data"; period: { from: string; to: string } }
    | { state: "pending" }
    | null;
}

export interface AnalyticsSettings {
  analytics_consent_mode: "notice" | "opt_in";
  traffic: { availability: "available" | "activation_required" };
}

const o = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;
const s = (orgId: string, storeId: string) => `${o(orgId)}/stores/${encodeURIComponent(storeId)}`;

export interface ReportParams {
  from?: string;
  to?: string;
  dim?: Dim;
}

export const analyticsApi = {
  report: (orgId: string, storeId: string, topic: Topic, p: ReportParams) =>
    request<Report>("GET", `${s(orgId, storeId)}/reports/${topic}`, { query: { ...p } }),
  exportReport: (
    orgId: string,
    storeId: string,
    topic: Topic,
    p: ReportParams,
    idempotencyKey: string,
  ) =>
    request<ReportExport>("POST", `${s(orgId, storeId)}/reports/${topic}/export`, {
      body: p,
      idempotencyKey,
    }),
  exports: (orgId: string, storeId: string) =>
    request<Page<ReportExport>>("GET", `${s(orgId, storeId)}/reports/exports`),
  exportStatus: (orgId: string, storeId: string, id: string) =>
    request<ReportExport>("GET", `${s(orgId, storeId)}/reports/exports/${encodeURIComponent(id)}`),
  layout: (orgId: string) => request<Layout>("GET", `${o(orgId)}/dashboard-layout`),
  saveLayout: (orgId: string, widgets: WidgetId[]) =>
    request<Layout>("PUT", `${o(orgId)}/dashboard-layout`, { body: { widgets } }),
  settings: (orgId: string, storeId: string) =>
    request<AnalyticsSettings>("GET", `${s(orgId, storeId)}/analytics/settings`),
  saveSettings: (
    orgId: string,
    storeId: string,
    mode: AnalyticsSettings["analytics_consent_mode"],
  ) =>
    request<{ analytics_consent_mode: string }>("PUT", `${s(orgId, storeId)}/analytics/settings`, {
      body: { analytics_consent_mode: mode },
    }),
  leaderboard: (orgId: string) => request<Leaderboard>("GET", `${o(orgId)}/leaderboard`),
  setLeaderboard: (orgId: string, optedIn: boolean) =>
    request<Leaderboard>("PUT", `${o(orgId)}/leaderboard`, { body: { opted_in: optedIn } }),
};

export const analyticsKeys = {
  report: (o: string, st: string, topic: Topic, p: ReportParams) =>
    ["analytics", o, st, "report", topic, p.from ?? "", p.to ?? "", p.dim ?? ""] as const,
  exports: (o: string, st: string) => ["analytics", o, st, "exports"] as const,
  layout: (o: string) => ["analytics", o, "layout"] as const,
  settings: (o: string, st: string) => ["analytics", o, st, "settings"] as const,
  leaderboard: (o: string) => ["analytics", o, "leaderboard"] as const,
};

export function useReport(o: string, st: string, topic: Topic, p: ReportParams, enabled = true) {
  return useQuery({
    queryKey: analyticsKeys.report(o, st, topic, p),
    queryFn: () => analyticsApi.report(o, st, topic, p),
    enabled,
    retry: false,
  });
}

export function useLayout(o: string) {
  return useQuery({ queryKey: analyticsKeys.layout(o), queryFn: () => analyticsApi.layout(o) });
}

export function useLeaderboard(o: string, enabled = true) {
  return useQuery({
    queryKey: analyticsKeys.leaderboard(o),
    queryFn: () => analyticsApi.leaderboard(o),
    enabled,
  });
}
